/**
 * 🆕 Phase C6 (2026-05-04) — Helper centralisé pour afficher proprement
 * du texte qui peut contenir du HTML (descriptions de BT, notes de soumission…).
 *
 * Pourquoi : certaines descriptions sont stockées en HTML (issu d'un éditeur
 * riche). Quand elles sont injectées en `{texte}` dans React, elles s'affichent
 * en brut (`<h3><strong>...</strong></h3>`) au lieu d'être rendues.
 *
 * Stratégie :
 *   • Aucune dépendance externe.
 *   • Décode les entités HTML communes (&amp;, &lt;, &gt;, &nbsp;, &#39;, &quot;).
 *   • Strip toutes les balises (mais conserve le texte interne).
 *   • Compresse les espaces multiples.
 *   • Truncate optionnel pour les listes courtes (modale "Ordonner les BT").
 *
 * IMPORTANT : ne pas utiliser `dangerouslySetInnerHTML` à la place — on veut
 * du texte brut affichable, pas du HTML rendu (sécurité + lisibilité dans
 * les listes).
 *
 * @param {string} html - Le texte source (potentiellement HTML).
 * @param {object} [opts] - Options.
 * @param {number} [opts.maxLen] - Longueur max (ajoute "…" si dépassée).
 * @returns {string} Texte propre, sans balises ni entités encodées.
 */
export function stripHtml(html, opts = {}) {
  if (html == null) return '';
  let s = String(html);

  // 1. Remplacer <br>, </p>, </div>, </li> par des espaces (préserver lisibilité)
  s = s.replace(/<\/?(br|p|div|li|h[1-6])\s*\/?>/gi, ' ');

  // 2. Strip toutes les autres balises HTML
  s = s.replace(/<[^>]*>/g, '');

  // 3. Décoder les entités HTML communes
  s = s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    // Décodage générique pour &#NNN; et &#xHH;
    .replace(/&#(\d+);/g, (_, n) => {
      try { return String.fromCharCode(parseInt(n, 10)); } catch { return ''; }
    })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => {
      try { return String.fromCharCode(parseInt(h, 16)); } catch { return ''; }
    });

  // 4. Compresser les espaces / sauts de ligne multiples
  s = s.replace(/\s+/g, ' ').trim();

  // 5. Truncate
  const max = opts.maxLen;
  if (typeof max === 'number' && max > 0 && s.length > max) {
    s = s.slice(0, max).trimEnd() + '…';
  }
  return s;
}

export default stripHtml;

/**
 * 🆕 2026-05-08 — Convertit un HTML formaté (gras/italique/surligné/listes) en texte plat
 * MULTI-LIGNE pour les vues d'aperçu et d'impression du BT.
 * - Conserve les sauts de ligne (br, p, div, li, h*)
 * - Préfixe les <li> avec "• " ou "1. " selon le contexte (ul/ol)
 * - Supprime les balises de formatage (b/strong/i/em/mark/u/span)
 *
 * Différent de stripHtml() qui retourne une seule ligne (utilisé pour les titres tronqués).
 */
export function htmlToPlainText(html) {
  if (html == null) return '';
  let s = String(html);
  if (!s.trim()) return '';
  // Si pas de balises HTML, on retourne tel quel (avec retours-ligne)
  if (!/<[a-z][\s\S]*>/i.test(s)) return s;

  // 1. Numéroter les <li> à l'intérieur des <ol>
  s = s.replace(/<ol[^>]*>([\s\S]*?)<\/ol>/gi, (_, inner) => {
    let i = 0;
    const numbered = inner.replace(/<li[^>]*>/gi, () => {
      i += 1;
      return `\n${i}. `;
    });
    return `\n${numbered}\n`;
  });

  // 2. <ul>/<li> → puces "• "
  s = s.replace(/<li[^>]*>/gi, '\n• ');
  s = s.replace(/<\/li>/gi, '');
  s = s.replace(/<\/?ul[^>]*>/gi, '\n');

  // 3. Sauts de ligne sur p, div, br, h*
  s = s.replace(/<br\s*\/?>/gi, '\n');
  s = s.replace(/<\/(p|div|h[1-6])>/gi, '\n');
  s = s.replace(/<(p|div|h[1-6])[^>]*>/gi, '');

  // 4. Strip toutes les autres balises (formatting : b, strong, em, i, mark, u, span, etc.)
  s = s.replace(/<[^>]*>/g, '');

  // 5. Décoder entités
  s = s
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/&apos;/gi, "'")
    .replace(/&#(\d+);/g, (_, n) => { try { return String.fromCharCode(parseInt(n, 10)); } catch { return ''; } })
    .replace(/&#x([0-9a-f]+);/gi, (_, h) => { try { return String.fromCharCode(parseInt(h, 16)); } catch { return ''; } });

  // 6. Trim chaque ligne et compresser les sauts multiples
  s = s
    .split('\n')
    .map(line => line.replace(/[ \t]+/g, ' ').trim())
    .filter((line, idx, arr) => !(line === '' && arr[idx - 1] === '')) // pas plus de 1 ligne vide consécutive
    .join('\n')
    .trim();
  return s;
}
